// Static fitting-type metadata.
//
// Canonical `name` strings confirmed against SF on 2026-06-04 — the names
// here must match the SF Apex /Availability fitting record names exactly,
// otherwise the call 400s (e.g., the earlier `Full Bag w/o Putter` issue).
// Source of truth: the legacy widget's dataLayer `e.detail.fittingSelection`.
//
// Per-country pricing/duration/availability comes from the CC "Booking Flow
// Review of Needs" requirements sheet (2026-08). Where a country column was
// blank in the sheet, we fall through to the top-level US-default values.
// Canada is currently the US menu at US pricing pending its own spec.

import type { FittingType, Location } from '../types';
import type { CountryCode } from '../config/countries';
import { promoWindowFor } from './promoWindows';

// Quantum Leap Challenge launch timestamp — 2026-08-01 midnight Central.
// Matches the prod v2 bundle constant. Promo fittings with this startsAt
// are hidden before this moment.
const QL_STARTS_AT = '2026-08-01T00:00:00-05:00';

// The Challenge handover. Quantum Leap ends as the Iron Shaft Challenge starts,
// so the two never appear together and there is no gap between them. One
// timestamp rather than two, because the whole point is that they are the same
// moment — CC described it as "the Callaway Quantum Leap Challenge ends in the
// morning, and we are starting a new fitting type".
const CHALLENGE_HANDOVER = '2026-09-01T00:00:00-05:00';

// Iron Shaft Challenge end. Launched as September 1–30; on 2026-10-05 CC
// reported it unbookable in every market because the window here had lapsed
// while SF's promo records had been extended "valid through 10/17" (Bill, via
// Dan Parks). `endsAt` is exclusive, so this is midnight at the end of the
// 17th, Central.
//
// These constants are now the FALLBACK. The live dates come from
// promo-windows.json published beside the bundle (see data/promoWindows.ts),
// which is what to change when CC extends a promotion; keep this constant in
// step so a visitor who could not fetch the file sees the same thing.
// Salesforce is not a source: the promotion it attaches to each fitting is the
// store's generic CUSTOMFIT record, verified in production 2026-10-05.
//
// 2026-10-17: ended a day early, in every market at once. The window is one
// instant for all markets, and midnight Central is 4 pm Sunday in Sydney;
// Australia's "Free Club with Qualifying Purchase" launched on the 18th local
// time and CC asked for the free Iron Shaft fitting to be gone before it (Dan
// Parks). Per-market windows would need a file-format change.
const ISC_ENDS_AT = '2026-10-17T00:00:00-05:00';

// Authoring order IS the rendered order.
//
// A longest-to-shortest sort shipped here on 2026-08-22, from a line in the UK
// review. CC answered it directly on 2026-08-24: "Not at this time. We're
// ordering the fitting types with a marketing strategy in mind and want to keep
// this consistent across all sites." So the order is merchandising, owned by
// CC, and it is expressed by the sequence of this array — do not sort it.
//
// Visibility is driven by promo/startsAt in visibleFittingTypes().
export const FITTING_TYPES: FittingType[] = [
  {
    // Top of the list, by request: "we need it to show in the morning at the
    // top of the list". Authoring order is the rendered order — see the note
    // above — so position here is the whole mechanism.
    id: 'iron-shaft-challenge',
    // EXACT Salesforce Product2.Name. Verified against prod /fittings on
    // 2026-08-31 for Indianapolis, Royal Oak, Chicago and Scottsdale: the
    // product exists as 'Iron Shaft Challenge', 31 minutes, price 0.
    name: 'Iron Shaft Challenge',
    description:
      'A free head-to-head challenge: your current iron shaft against one our fitter '
      + 'selects for you on the launch monitor. Thirty minutes, on its own or added to a '
      + 'fitting you have already booked.',
    durationLabel: '30 Minutes',
    durationMinutes: 30,
    pricingLabel: 'FREE',
    badge: 'FREE',
    promo: true,
    startsAt: CHALLENGE_HANDOVER,
    endsAt: ISC_ENDS_AT,
    // US, UK, Canada and Australia. Australia was held back at launch because
    // Salesforce did not yet carry the product there; it was enabled 2026-09-09
    // once CC confirmed the AU rollout ('Iron Shaft Challenge - AUS').
    //
    // Enabled while the Salesforce rollout was still in progress, deliberately:
    // three of nine UK/CA stores did not yet stock the product, and a store
    // without it renders an empty slot grid — "no days available" on a calendar
    // that is open. That was accepted so the markets could be tested in
    // production while CC populated the rest. If it is still showing empty at a
    // named store, that store is missing the product, not broken.
    //
    // The names are country-suffixed and, unlike every earlier product, use a
    // hyphen: 'Iron Shaft Challenge - UK', 'Iron Shaft Challenge - Canada'.
    // stripCountrySuffix() tolerates the separator; see fittingNames.ts.
    availableIn: ['us', 'uk', 'ca', 'au'],
  },
  {
    id: 'quantum-leap-challenge',
    name: 'Callaway Quantum Leap Challenge',
    description: 'Test your current gamer driver and ball against the latest Callaway driver and ball',
    durationLabel: '30 Minutes',
    durationMinutes: 30,
    pricingLabel: 'FREE',
    badge: 'FREE',
    promo: true,
    startsAt: QL_STARTS_AT,
    // Ends exactly as the Iron Shaft Challenge begins.
    endsAt: CHALLENGE_HANDOVER,
    availableIn: ['us'],  // Callaway partnership is US-only per current scope
  },
  {
    id: 'full-bag',
    name: 'Full Bag',
    description: 'The most comprehensive fitting in the industry, covering all woods, irons, wedges, and putter.',
    durationLabel: '3 hours',
    durationMinutes: 180,
    pricingLabel: 'FREE w/ custom club purchase or $400 flat fitting fee',
    pricingLabelPromo: '$100 w/ custom club purchase or $400 flat fitting fee',
    badge: 'MOST COMPREHENSIVE',
    // UK reinstated 2026-08-17 on review feedback — the earlier sheet omitted
    // it, but SF has offered "Full Bag UK" (3.5 hours, £250) all along, so the
    // widget was hiding a fitting customers could otherwise book.
    availableIn: ['us', 'ca', 'au', 'uk'],
    pricingByCountry: {
      au: {
        pricingLabel: '$100 w/ custom club purchase or $400 AUD flat fitting fee',
        durationLabel: '4 hours',
        durationMinutes: 240,
      },
      uk: {
        pricingLabel: '£100 w/ custom club purchase or £250 flat fitting fee',
        durationLabel: '3.5 hours',
        durationMinutes: 210,
      },
    },
  },
  {
    id: 'driver',
    name: 'Driver',
    description: 'Find more power off the tee with this focused fitting.',
    durationLabel: '1.5 hours',
    durationMinutes: 90,
    pricingLabel: 'FREE w/ custom club purchase or $175 flat fitting fee',
    pricingLabelPromo: '$50 w/ custom club purchase or $175 flat fitting fee',
    badge: 'POPULAR',
    pricingByCountry: {
      uk: { pricingLabel: '£50 w/ custom club purchase or £100 flat fitting fee' },
      au: { pricingLabel: '$50 w/ custom club purchase or $150 AUD flat fitting fee' },
    },
  },
  {
    id: 'iron',
    // SF canonical is `Iron`; production widget displays `Iron + Wedge`.
    name: 'Iron',
    displayName: 'Iron + Wedge',
    description: 'Improve your control with a broad selection of the best clubheads and shafts available.',
    durationLabel: '1.5 hours',
    durationMinutes: 90,
    pricingLabel: 'FREE w/ custom club purchase or $175 flat fitting fee',
    pricingLabelPromo: '$50 w/ custom club purchase or $175 flat fitting fee',
    badge: 'POPULAR',
    pricingByCountry: {
      uk: {
        displayName: 'Irons + Wedge',
        pricingLabel: '£50 w/ custom club purchase or £100 flat fitting fee',
      },
      au: {
        pricingLabel: '$50 w/ custom club purchase or $200 AUD flat fitting fee',
        durationLabel: '2 hours',
        durationMinutes: 120,
      },
    },
  },
  {
    id: 'putter',
    name: 'Putter',
    description: 'Get the ball rolling properly and on-line with this unique fitting.',
    durationLabel: '1 hour',
    durationMinutes: 60,
    pricingLabel: 'FREE w/ custom club purchase or $125 flat fitting fee',
    pricingLabelPromo: '$50 w/ custom club purchase or $125 flat fitting fee',
    pricingByCountry: {
      uk: { pricingLabel: '£50 w/ custom club purchase or £75 flat fitting fee' },
      // AU putter is its own duration and price — production lists "Putter
      // AUS" at 1.5 hours / $150, not the 1 hour / $100 the shared default
      // assumed. (The QA org says $149; production is the authority on price.)
      au: {
        pricingLabel: '$50 w/ custom club purchase or $150 AUD flat fitting fee',
        durationLabel: '1.5 hours',
        durationMinutes: 90,
      },
    },
  },
  {
    id: 'full-bag-wo-putter',
    // SF canonical is `Full Bag (No Putter)`; product/UX label is `Full Bag Minus Putter`.
    name: 'Full Bag (No Putter)',
    displayName: 'Full Bag Minus Putter',
    description: 'Same as the Full Bag Fitting minus the flatstick.',
    durationLabel: '2.5 hours',
    durationMinutes: 150,
    pricingLabel: 'FREE w/ custom club purchase or $325 flat fitting fee',
    pricingLabelPromo: '$100 w/ custom club purchase or $325 flat fitting fee',
    pricingByCountry: {
      uk: {
        displayName: 'Full Bag - No Putter',
        // PERFECTFITUK's terms price "a full bag fitting" at £100 with a
        // custom equipment purchase and "any other fitting type" at £50.
        // There are two full-bag products, and the terms do not say which
        // side of that line the no-putter variant falls on; read here as a
        // full bag. Confirm with CC before UK launch.
        pricingLabel: '£100 w/ custom club purchase or £200 flat fitting fee',
        durationLabel: '3 hours',
        durationMinutes: 180,
      },
      au: {
        pricingLabel: '$100 w/ custom club purchase or $300 AUD flat fitting fee',
        durationLabel: '3 hours',
        durationMinutes: 180,
      },
    },
  },
  {
    id: 'full-bag-start',
    // SF canonical is `Full Bag - Start`; production widget displays `Full Bag Split Session`.
    name: 'Full Bag - Start',
    displayName: 'Full Bag Split Session',
    description: 'A partial fitting allows golfers to fit their comprehensive fitting into multiple sessions.',
    durationLabel: '2 hours',
    durationMinutes: 120,
    pricingLabel: 'FREE w/ custom club purchase or $400 flat fitting fee',
    pricingLabelPromo: '$100 w/ custom club purchase or $400 flat fitting fee',
    availableIn: ['us', 'ca'],  // UK/AU sheets don't list a split-session variant
  },
  {
    id: 'long-game',
    // SF canonical is `Complete Long Game`; product/UX label is `Long Game`.
    name: 'Complete Long Game',
    displayName: 'Long Game',
    description: 'Combine your driver, fairway woods, and hybrids in this value-based fitting option.',
    durationLabel: '2 hours',
    durationMinutes: 120,
    pricingLabel: 'FREE w/ custom club purchase or $250 flat fitting fee',
    pricingLabelPromo: '$50 w/ custom club purchase or $250 flat fitting fee',
    pricingByCountry: {
      uk: {
        displayName: 'Complete Long Game',
        pricingLabel: '£50 w/ custom club purchase or £150 flat fitting fee',
      },
      au: {
        pricingLabel: '$50 w/ custom club purchase or $250 AUD flat fitting fee',
        durationLabel: '2.5 hours',
        durationMinutes: 150,
      },
    },
  },
  {
    id: 'fairway-wood-hybrid',
    // SF canonical is `Fairway Wood/Hybrid`; production widget displays `Fairway Wood + Hybrid`.
    name: 'Fairway Wood/Hybrid',
    displayName: 'Fairway Wood + Hybrid',
    description: 'Attack every lie imaginable after dialing in these clubs for optimal launch angle and swing speed.',
    durationLabel: '1 hour',
    durationMinutes: 60,
    pricingLabel: 'FREE w/ custom club purchase or $125 flat fitting fee',
    pricingLabelPromo: '$50 w/ custom club purchase or $125 flat fitting fee',
    pricingByCountry: {
      uk: {
        displayName: 'Fairways + Hybrid',
        pricingLabel: '£50 w/ custom club purchase or £75 flat fitting fee',
      },
      au: {
        pricingLabel: '$50 w/ custom club purchase or $150 AUD flat fitting fee',
        durationLabel: '1.5 hours',
        durationMinutes: 90,
      },
    },
  },
  {
    id: 'wedge',
    name: 'Wedge',
    description: 'Check in on your lofts, bounce and gapping with this short game saver.',
    durationLabel: '1 hour',
    durationMinutes: 60,
    pricingLabel: 'FREE w/ custom club purchase or $100 flat fitting fee',
    pricingLabelPromo: '$50 w/ custom club purchase or $100 flat fitting fee',
    pricingByCountry: {
      uk: { pricingLabel: '£50 w/ custom club purchase or £75 flat fitting fee' },
      au: { pricingLabel: '$50 w/ custom club purchase or $100 AUD flat fitting fee' },
    },
  },
];

// ─── Helpers ───────────────────────────────────────────────────────────────

/** True when the CC promo window (Callaway QL + $100/$50 pricing) is active.
 * Config levers on window.CC_WIDGET_CONFIG:
 *   promoFittings: false → hide all promo:true fittings + revert pricing (kill switch)
 *   promoFittingsPreview: true → bypass startsAt so QA sees the promo pre-launch */
/**
 * Preview mode — evaluate the fitting list at a different moment in time.
 *
 * This deliberately does NOT mean "ignore the dates". The first version did,
 * and it showed the Iron Shaft Challenge and the Callaway Quantum Leap
 * Challenge side by side — which is a state that never actually occurs, since
 * one ends exactly as the other begins. A preview that shows an impossible
 * arrangement cannot tell you whether the real one is right.
 *
 * So it moves the clock instead. Everything else stays honest: startsAt and
 * endsAt are both applied, at the previewed instant rather than the real one,
 * and the result is exactly what customers will see at that moment.
 *
 *   ?ccPromoPreview=1            the next scheduled change — usually what you want
 *   ?ccPromoPreview=2026-09-15   a specific day
 *   ?ccPromoPreview=0            off, without editing the URL
 *
 * Key match is case-insensitive, matching the Web_Promo convention next door.
 *
 * ── What this does and does not expose ──────────────────────────────────────
 * It shifts the clock only. It does not enable promo fittings that are switched
 * off (`promoFittings: false` still wins), does not change pricing, and cannot
 * conjure a Salesforce product. A fitting whose product exists WILL take a real
 * booking through it — which is the point when checking availability, and the
 * reason to treat the parameter as internal rather than secret.
 */
function promoPreviewInstant(now: Date): Date | null {
  if (typeof window === 'undefined') return null;

  const raw = (() => {
    if ((window as { CC_WIDGET_CONFIG?: { promoFittingsPreview?: boolean } }).CC_WIDGET_CONFIG
      ?.promoFittingsPreview === true) return '1';
    if (typeof location === 'undefined' || !location.search) return null;
    const params = new URLSearchParams(location.search);
    for (const [k, v] of params) {
      if (k.toLowerCase() === 'ccpromopreview') return v.trim();
    }
    return null;
  })();

  if (raw === null) return null;

  const val = raw.toLowerCase();
  if (val === '0' || val === 'false' || val === 'no') return null;

  // An explicit date wins. Bare date strings are read as local midnight rather
  // than UTC, so ?ccPromoPreview=2026-09-01 means the 1st where the customer is.
  if (val !== '' && val !== '1' && val !== 'true' && val !== 'yes') {
    const explicit = /^\d{4}-\d{2}-\d{2}$/.test(raw)
      ? new Date(`${raw}T12:00:00`)
      : new Date(raw);
    if (!Number.isNaN(explicit.getTime())) return explicit;
    return null;   // Unparseable date: preview off rather than silently "now".
  }

  // Default: the next scheduled change. The earliest promo boundary still in
  // the future — a startsAt that has not arrived, or an endsAt not yet reached.
  const nowMs = now.getTime();
  const upcoming = FITTING_TYPES
    .filter((ft) => ft.promo)
    .map(effectivePromoWindow)
    .flatMap((w) => [w.startsAt, w.endsAt])
    .filter((d): d is string => typeof d === 'string')
    .map((d) => Date.parse(d))
    .filter((t) => !Number.isNaN(t) && t > nowMs)
    .sort((a, b) => a - b);

  return upcoming.length ? new Date(upcoming[0]) : now;
}

/**
 * The window in force for a promo fitting: the published file's dates where
 * it has them, the authored constants otherwise, side by side.
 */
export function effectivePromoWindow(ft: FittingType): { startsAt?: string; endsAt?: string } {
  const published = promoWindowFor(ft.name);
  return {
    startsAt: published?.startsAt ?? ft.startsAt,
    endsAt: published?.endsAt ?? ft.endsAt,
  };
}

function isPromoFittingsEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  return (window as { CC_WIDGET_CONFIG?: { promoFittings?: boolean } }).CC_WIDGET_CONFIG
    ?.promoFittings !== false;
}

export function isPromoWindowActive(now: Date = new Date()): boolean {
  // Same clock shift as the fitting list, so previewed pricing matches the
  // previewed fittings rather than the two disagreeing about what day it is.
  const at = promoPreviewInstant(now) ?? now;
  return at.getTime() >= Date.parse(QL_STARTS_AT);
}

/** Country the caller cares about. Falls back to 'us' when null/undefined so
 * every call site can safely pass state.country (which is null pre-engage). */
function normalizeCountry(c: string | null | undefined): CountryCode {
  const code = (c ?? 'us').trim().toLowerCase();
  return (['us', 'uk', 'ca', 'au'] as const).includes(code as CountryCode)
    ? (code as CountryCode)
    : 'us';
}

/**
 * Effective per-country display fields for a fitting. Merges the base
 * fields with any country override. Callers should render from THIS
 * object rather than the raw ft.pricingLabel / ft.displayName / etc.
 */
export interface FittingDisplay {
  displayName: string;
  durationLabel: string;
  durationMinutes: number;
  pricingLabel: string;
  pricingLabelPromo?: string;
}

export function fittingDisplayFor(ft: FittingType, country: string | null | undefined): FittingDisplay {
  const code = normalizeCountry(country);
  const override = ft.pricingByCountry?.[code];
  // Pricing rule: a country override that defines pricingLabel OWNS the
  // pricing story for that country — including promo behavior. If the
  // override has no pricingLabelPromo, that country does NOT have a promo
  // variant. Never mix the country's base with US's promo (that produced
  // the "£100 base + $50 promo" bug where UK cards rendered US dollars).
  const hasCountryPricing = !!override?.pricingLabel;
  return {
    displayName: override?.displayName ?? ft.displayName ?? ft.name,
    durationLabel: override?.durationLabel ?? ft.durationLabel,
    durationMinutes: override?.durationMinutes ?? ft.durationMinutes,
    pricingLabel: hasCountryPricing ? override!.pricingLabel! : ft.pricingLabel,
    pricingLabelPromo: hasCountryPricing ? override!.pricingLabelPromo : ft.pricingLabelPromo,
  };
}

// ─── Per-location fitting rules ────────────────────────────────────────────
// Some stores physically can't host every fitting type (e.g. Mobile Fitting
// Studios don't have a putting green). Rather than probing SF /availability
// per fitting per store to discover this at runtime, encode the known
// physical/product constraints here. Rules match on the location and return
// a set of SF-canonical fitting names to hide.
//
// Long-term this should come from Salesforce (Store__c should carry a list
// of supported fittings), at which point Location gets a `supportedFittings`
// field and this rule table goes away. Today the exclusions are stable
// enough to hardcode.

interface LocationFittingRule {
  matches: (loc: Pick<Location, 'name'>) => boolean;
  /** SF-canonical fitting names to hide (must match FittingType.name exactly). */
  excludes: string[];
}

const LOCATION_FITTING_RULES: LocationFittingRule[] = [
  {
    // "Mobile Fitting Studio 2/3/4/5/6..." — truck-mounted fitting units
    // lack the putting-green space required for a putter fitting.
    matches: (loc) => /^Mobile Fitting Studio\b/i.test(loc.name),
    excludes: ['Putter'],
  },
];

function fittingsExcludedForLocation(location: Pick<Location, 'name'> | null | undefined): Set<string> {
  if (!location) return new Set();
  const set = new Set<string>();
  for (const rule of LOCATION_FITTING_RULES) {
    if (rule.matches(location)) {
      for (const name of rule.excludes) set.add(name);
    }
  }
  return set;
}

/** Fittings available in the requested country, gated for promo-window rules
 * AND filtered against any per-location exclusions when a location is set. */
export function visibleFittingTypes(
  country: string | null | undefined,
  location?: Pick<Location, 'name'> | null,
  now: Date = new Date(),
): FittingType[] {
  const code = normalizeCountry(country);
  const enabled = isPromoFittingsEnabled();
  // Preview shifts the clock; it does not suspend the rules. See
  // promoPreviewInstant().
  const nowMs = (promoPreviewInstant(now) ?? now).getTime();
  const excluded = fittingsExcludedForLocation(location);
  const visible = FITTING_TYPES.filter((ft) => {
    // Per-location physical/product constraints (e.g. Mobile Studios have no putter)
    if (excluded.has(ft.name)) return false;
    // Country availability: absent availableIn = all countries. Present +
    // excludes → hidden in that country.
    if (ft.availableIn && !ft.availableIn.includes(code)) return false;
    // Promo-window gate (same as v2 prod)
    if (!ft.promo) return true;
    if (!enabled) return false;
    const { startsAt, endsAt } = effectivePromoWindow(ft);
    if (startsAt && nowMs < Date.parse(startsAt)) return false;
    if (endsAt && nowMs >= Date.parse(endsAt)) return false;
    return true;
  });

  return visible;
}


/** Pricing label to render, respecting country override + promo-window swap. */
export function pricingLabelFor(ft: FittingType, country: string | null | undefined, now: Date = new Date()): string {
  const d = fittingDisplayFor(ft, country);
  return isPromoWindowActive(now) && d.pricingLabelPromo ? d.pricingLabelPromo : d.pricingLabel;
}
